import React, {useCallback, useMemo} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import { Card } from "react-bootstrap";
import {useIntl} from 'react-intl';
import Level3Title  from '../../../../app/common/components/title/Level3Title';
import TableMetronic from '../../../../app/common/components/table/TableMetronic';
import { useGetTelephonyUsage } from '../../Hooks/TelephonieManager';
import { readablePriceHelper } from '../../Helpers/readablePrice';
import { readableTelephoneHelper } from '../../Helpers/readableTelephone';
import { seconds2durationHelper } from '../../Helpers/seconds2duration';
import {useNavigate} from 'react-router-dom';
import {getTelephonyDetailsPathname} from '../../Helpers/ServiceHelper';
import {useSearchParams} from 'react-router-dom';

interface ConsommationsDetailleesVoipInterface {
    selectedMonth: string;
    selectedOrder: string;
    serviceId?: string;
}

const ConsommationsDetailleesVoip = ({selectedMonth, selectedOrder, serviceId}: ConsommationsDetailleesVoipInterface) => {
    const intl = useIntl();
    const readablePriceHelperInstance = useMemo(() => new readablePriceHelper(), []);
    const readableTelephoneHelperInstance = useMemo(() => new readableTelephoneHelper(), []);
    const seconds2durationHelperInstance = useMemo(() => new seconds2durationHelper(), []);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();


    const {data, isLoading} = useGetTelephonyUsage({
        month: selectedMonth,
        order: selectedOrder,
        category: "centrex",
        summary: true,
        info: selectedOrder,
        serviceId,
    });

    const resumeAppelVoIP = useMemo(() => data?.calls ?? [], [data]);

    const { totalDuration, totalPrice } = useMemo(() => {
        let totalD = 0;
        let totalP = 0;
        resumeAppelVoIP.forEach((appel: any) => {
            totalD += parseFloat(appel.time ?? 0);
            totalP += parseFloat(appel.price ?? 0);
        });
        return { totalDuration: totalD, totalPrice: totalP };
    }, [resumeAppelVoIP]);

    
    
    const goToDetailAppels = useCallback(
        (row: any) => {
            const params = new URLSearchParams(searchParams);
            const byAccount = selectedOrder === 'compte';
            params.set('type', 'voip');
            params.set('destination', byAccount ? '' : String(row.idDestination ?? ''));
            params.set('month', selectedMonth);
            params.set('caller', byAccount ? row.caller : '');
            params.set('label', byAccount ? row.sipAccount : row.dest);
            navigate({
                pathname: `${getTelephonyDetailsPathname(serviceId ?? '')}/appels`,
                search: params.toString(),
            });
        },
        [navigate, searchParams, selectedMonth, selectedOrder, serviceId]
    );

    const columns = useMemo<ColumnDef<any>[]>(() => {
        return [
            {
                id: 'label',
                header: intl.formatMessage({id: selectedOrder === "compte" ? 'TELEPHONIE.CONSO.ORDER.COMPTE' : 'TELEPHONIE.CONSO.ORDER.DESTINATION'}),
                accessorFn: (row) => (selectedOrder === "compte" ? readableTelephoneHelperInstance.readableTelephone(row.sipAccount) : row.dest),
            },
            {
                header: intl.formatMessage({id: 'TELEPHONIE.CONSO.COL.DUREE_FACTUREE'}),
                accessorKey: 'time',
                cell: (info) => {
                    return seconds2durationHelperInstance.seconds2duration(info.row.original.time ?? 0, true);
                },
            },
            {
                header: intl.formatMessage({id: 'TELEPHONIE.CONSO.COL.EUR'}),
                accessorKey: 'price',
                cell: (info) => {
                    return readablePriceHelperInstance.readablePrice(info.row.original.price ?? 0) + " €";
                }
            },
            {
                id: 'detailArrow',
                header: '',
                size: 40,
                enableSorting: false,
                cell: () => (
                    <div className="d-flex align-items-center justify-content-end pe-3">
                        <i className="bi bi-chevron-right text-muted" />
                    </div>
                ),
            }
            ];
    }, [intl, selectedOrder, readableTelephoneHelperInstance, seconds2durationHelperInstance, readablePriceHelperInstance]);

    return (
        <>
            { !isLoading && resumeAppelVoIP.length === 0 && (<div className="text-muted">
                <p className="mb-1">{intl.formatMessage({id: 'TELEPHONIE.CONSO.VOIP.EMPTY'})}</p>
            </div>)}

            { (isLoading || resumeAppelVoIP.length > 0)  && (<Card className="col-md-12">
                <Card.Header className="border-0 pt-6">
                <Card.Title>
                    <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.CONSO.VOIP.TITLE'})} />
                </Card.Title>
                </Card.Header>

                <Card.Body className="pt-2">
                <TableMetronic<any>
                    title={intl.formatMessage(
                        {id: 'TELEPHONIE.CONSO.VOIP.TABLE_TITLE'},
                        {
                            duration: seconds2durationHelperInstance.seconds2duration(totalDuration, true),
                            price: readablePriceHelperInstance.readablePrice(totalPrice),
                        }
                    )}
                    data={resumeAppelVoIP}
                    columns={columns}
                    rowClickable={() => true}
                    onRowClick={goToDetailAppels}
                    isLoading={isLoading}
                    searchBar
                    pagination
                />
                </Card.Body>
            </Card>)}
        </>
    );
};

export default ConsommationsDetailleesVoip;
